import { useState, useEffect } from 'react';
import AdminActivityPanel from './AdminActivityPanel';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  ShieldCheck, Plus, Trash, ToggleLeft, ToggleRight, Megaphone, Users, UserPlus, Copy
} from '@phosphor-icons/react';

const ACCESS_DAYS_OPTIONS = [7, 14, 30, 90, 365];

// EFFECTIVE-PLAN-SESSION-ONBOARDING-001, part E: `email_sent=true` never
// meant "delivered" — only "the provider accepted the API call". This
// maps the richer `email_status` to the correct, non-misleading wording;
// the setup link is always shown regardless of the outcome.
const EMAIL_STATUS_LABEL = {
  not_requested: 'Invio email disattivato per questo utente.',
  configuration_missing: 'Email non inviata: nessun provider email configurato. Usa il link.',
  provider_accepted: 'Email presa in carico dal provider.',
  provider_rejected: 'Email non inviata: usa il link.',
  failed: 'Email non inviata: usa il link.',
};

export default function AdminConsole() {
  const { api, user } = useAuth();
  const [powerUsers, setPowerUsers] = useState([]);
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [usersSummary, setUsersSummary] = useState({ total_users: 0, subscriber_users: 0, free_users: 0 });
  const [usersList, setUsersList] = useState([]);
  const [puEmail, setPuEmail] = useState('');
  const [puPlan, setPuPlan] = useState('strategist');
  const [puDays, setPuDays] = useState(30);
  const [puNotes, setPuNotes] = useState('');
  const [rnTitle, setRnTitle] = useState('');
  const [rnBody, setRnBody] = useState('');
  const [rnVersion, setRnVersion] = useState('');
  const [loading, setLoading] = useState(true);
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPlan, setNewUserPlan] = useState('free');
  const [newUserDays, setNewUserDays] = useState(30);
  const [newUserSendEmail, setNewUserSendEmail] = useState(true);
  const [newUserLanguage, setNewUserLanguage] = useState('it');
  const [newUserResult, setNewUserResult] = useState(null);
  const [newUserError, setNewUserError] = useState('');
  const [newUserListRefreshFailed, setNewUserListRefreshFailed] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [resendingWelcomeId, setResendingWelcomeId] = useState(null);

  const resendWelcomeEmail = async (userId) => {
    setResendingWelcomeId(userId);
    try {
      const { data } = await api.post(`/admin/users/${userId}/resend-welcome-email`);
      setUsersList(prev => prev.map(u => (u.id === userId ? { ...u, welcome_email_status: data.email_status } : u)));
      toast.success(data.email_sent ? 'Email di benvenuto inviata.' : `Email non inviata (${data.email_status}).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante il reinvio.');
    }
    setResendingWelcomeId(null);
  };

  useEffect(() => {
    Promise.all([
      api.get('/admin/users').then(r => {
        setUsersSummary(r.data?.summary || { total_users: 0, subscriber_users: 0, free_users: 0 });
        setUsersList(r.data?.users || []);
      }).catch(() => {}),
      api.get('/admin/power-users').then(r => setPowerUsers(r.data)).catch(() => {}),
      api.get('/release-notes').then(r => setReleaseNotes(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [api]);

  const addPowerUser = async () => {
    if (!puEmail.trim()) return;
    await api.post('/admin/power-users', { email: puEmail, plan: puPlan, days: puDays, notes: puNotes });
    const { data } = await api.get('/admin/power-users');
    setPowerUsers(data);
    setPuEmail(''); setPuNotes('');
  };

  const createUser = async () => {
    if (!newUserName.trim() || !newUserEmail.trim() || creatingUser) return;
    setCreatingUser(true);
    setNewUserError('');
    setNewUserResult(null);
    setNewUserListRefreshFailed(false);
    // ADMIN-USER-PROVISIONING false-error fix (EFFECTIVE-PLAN-SESSION-
    // ONBOARDING-001, part D): the creation POST's success is definitive
    // and must never be undone by a failure in the list-refresh GET that
    // follows it — the two calls get their own, independent error
    // handling so a refresh hiccup never paints a completed user
    // creation as a failure.
    let payload;
    try {
      payload = {
        name: newUserName,
        email: newUserEmail,
        access_plan: newUserPlan,
        send_email: newUserSendEmail,
        language: newUserLanguage,
      };
      if (newUserPlan !== 'free') payload.access_days = newUserDays;
      const { data } = await api.post('/admin/users', payload);
      setNewUserResult(data);
      setNewUserName('');
      setNewUserEmail('');
    } catch (e) {
      setNewUserError(e.response?.data?.detail || 'Errore durante la creazione dell\'utente.');
      setCreatingUser(false);
      return;
    }
    setCreatingUser(false);
    try {
      const usersRes = await api.get('/admin/users');
      setUsersSummary(usersRes.data?.summary || usersSummary);
      setUsersList(usersRes.data?.users || []);
    } catch {
      setNewUserListRefreshFailed(true);
    }
  };

  const copySetupLink = () => {
    if (!newUserResult?.setup_url) return;
    navigator.clipboard.writeText(newUserResult.setup_url);
    toast.success('Copiato!');
  };

  const togglePu = async (email) => {
    await api.post(`/admin/power-users/${email}/toggle`);
    const { data } = await api.get('/admin/power-users');
    setPowerUsers(data);
  };

  const deletePu = async (email) => {
    await api.delete(`/admin/power-users/${email}`);
    setPowerUsers(prev => prev.filter(p => p.email !== email));
  };

  const createNote = async () => {
    if (!rnTitle.trim() || !rnBody.trim()) return;
    const { data } = await api.post('/admin/release-notes', { title: rnTitle, body: rnBody, version: rnVersion });
    setReleaseNotes(prev => [data, ...prev]);
    setRnTitle(''); setRnBody(''); setRnVersion('');
  };

  const deleteNote = async (id) => {
    await api.delete(`/admin/release-notes/${id}`);
    setReleaseNotes(prev => prev.filter(n => n.id !== id));
  };

  const formatDate = (value) => {
    if (!value) return '—';
    try { return new Date(value).toLocaleDateString('it-IT'); } catch { return '—'; }
  };

  if (user?.role !== 'admin') return <p className="text-center text-[var(--text-muted)] py-12">Accesso riservato agli amministratori.</p>;

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold gradient-text mb-2" data-testid="admin-title">Admin Console</h1>
      <p className="text-[var(--text-secondary)] mb-8 text-sm">Gestisci utenti, sottoscrittori, power users e note di rilascio.</p>

      <AdminActivityPanel api={api} users={usersList} />

      {/* Registered Users */}
      <div className="card mb-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><Users size={20} /> Utenti registrati</h3>
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="p-3 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
            <p className="text-xs text-[var(--text-muted)]">Totale utenti</p>
            <p className="text-2xl font-bold">{usersSummary.total_users}</p>
          </div>
          <div className="p-3 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
            <p className="text-xs text-[var(--text-muted)]">Sottoscrittori</p>
            <p className="text-2xl font-bold">{usersSummary.subscriber_users}</p>
          </div>
          <div className="p-3 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
            <p className="text-xs text-[var(--text-muted)]">Free</p>
            <p className="text-2xl font-bold">{usersSummary.free_users}</p>
          </div>
        </div>
        {usersList.length > 0 ? (
          <div className="space-y-2">
            {usersList.map(u => (
              <div key={u.id} className="hook-item">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{u.name || 'Senza nome'} <span className="text-[var(--text-muted)] font-normal">· {u.email}</span></p>
                  <p className="text-xs text-[var(--text-muted)]">
                    Piano: {u.effective_plan}
                    {u.power_user_override ? ' (power user)' : ''}
                    {' | '}Registrato: {formatDate(u.created_at)}
                    {' | '}Attivato: {formatDate(u.plan_activated_at)}
                  </p>
                </div>
                <span className={`badge ${u.is_subscriber ? 'green' : 'orange'}`}>
                  {u.is_subscriber ? 'Sottoscrittore' : 'Free'}
                </span>
                {/* FREE-PLAN-WALLET-PUBLISHING-001A / part H: only offered
                    for an active account whose last welcome-email attempt
                    was NOT already provider_accepted. */}
                {u.account_active && u.welcome_email_status && u.welcome_email_status !== 'provider_accepted' && (
                  <button
                    className="btn-ghost text-xs py-1 px-2"
                    disabled={resendingWelcomeId === u.id}
                    onClick={() => resendWelcomeEmail(u.id)}
                  >
                    {resendingWelcomeId === u.id ? '...' : 'Reinvia email di benvenuto'}
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[var(--text-muted)]">Nessun utente trovato.</p>
        )}
      </div>

      {/* Create new user */}
      <div className="card mb-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><UserPlus size={20} /> Crea nuovo utente</h3>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <input
            className="input-dark text-sm py-2"
            placeholder="Nome"
            value={newUserName}
            onChange={e => setNewUserName(e.target.value)}
            style={{ paddingLeft: '0.75rem' }}
            data-testid="new-user-name-input"
          />
          <input
            className="input-dark text-sm py-2"
            placeholder="Email"
            value={newUserEmail}
            onChange={e => setNewUserEmail(e.target.value)}
            style={{ paddingLeft: '0.75rem' }}
            data-testid="new-user-email-input"
          />
          <select
            className="input-dark text-sm py-2"
            value={newUserPlan}
            onChange={e => setNewUserPlan(e.target.value)}
            style={{ paddingLeft: '0.75rem' }}
            data-testid="new-user-plan-select"
          >
            <option value="free">Free</option>
            <option value="creator">Creator</option>
            <option value="strategist">Strategist</option>
            <option value="custom">Custom</option>
          </select>
          {newUserPlan !== 'free' && (
            <select
              className="input-dark text-sm py-2"
              value={newUserDays}
              onChange={e => setNewUserDays(Number(e.target.value))}
              style={{ paddingLeft: '0.75rem' }}
              data-testid="new-user-days-select"
            >
              {ACCESS_DAYS_OPTIONS.map(days => (
                <option key={days} value={days}>{days} giorni</option>
              ))}
            </select>
          )}
          <select
            className="input-dark text-sm py-2"
            value={newUserLanguage}
            onChange={e => setNewUserLanguage(e.target.value)}
            style={{ paddingLeft: '0.75rem' }}
            data-testid="new-user-language-select"
          >
            <option value="it">Italiano</option>
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="fr">Français</option>
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mb-4">
          <input
            type="checkbox"
            checked={newUserSendEmail}
            onChange={e => setNewUserSendEmail(e.target.checked)}
            data-testid="new-user-send-email-checkbox"
          />
          Invia email di configurazione
        </label>
        <button
          className="btn-gradient text-sm"
          onClick={createUser}
          disabled={creatingUser}
          data-testid="create-user-btn"
        >
          <Plus size={16} /> {creatingUser ? 'Creazione...' : 'Crea utente'}
        </button>

        {newUserError && (
          <p className="text-sm text-[var(--accent-pink)] mt-3" data-testid="new-user-error">{newUserError}</p>
        )}

        {newUserResult && (
          <div className="mt-4 p-4 rounded-lg space-y-2" style={{ background: 'var(--bg-secondary)' }} data-testid="new-user-result">
            <p className="text-sm font-medium">
              {newUserResult.recovered_pending ? 'Recupero account esistente' : 'Utente creato'}
              <span className="text-[var(--text-muted)] font-normal"> · {newUserResult.user?.email}</span>
            </p>
            <p className="text-xs text-[var(--text-muted)]" data-testid="new-user-email-status">
              {EMAIL_STATUS_LABEL[newUserResult.email_status] || (newUserResult.email_sent ? 'inviata' : 'non inviata')}
            </p>
            {newUserResult.power_user && (
              <p className="text-xs text-[var(--text-muted)]">
                Accesso {newUserResult.power_user.plan} assegnato fino al {new Date(newUserResult.power_user.expires_at).toLocaleDateString('it-IT')}
              </p>
            )}
            <button className="btn-ghost text-sm" onClick={copySetupLink} data-testid="copy-setup-link-btn">
              <Copy size={14} /> Copia link
            </button>
            {newUserListRefreshFailed && (
              <p className="text-xs text-[var(--accent-orange,#f59e0b)]" data-testid="new-user-list-refresh-warning">
                Utente creato, ma elenco non aggiornato.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Power Users */}
      <div className="card mb-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><ShieldCheck size={20} /> Power Users</h3>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <input className="input-dark text-sm py-2" placeholder="Email utente" value={puEmail} onChange={e => setPuEmail(e.target.value)} style={{ paddingLeft: '0.75rem' }} />
          <select className="input-dark text-sm py-2" value={puPlan} onChange={e => setPuPlan(e.target.value)} style={{ paddingLeft: '0.75rem' }}>
            <option value="creator">Creator</option>
            <option value="strategist">Strategist</option>
            <option value="custom">Custom</option>
          </select>
          <select className="input-dark text-sm py-2" value={puDays} onChange={e => setPuDays(Number(e.target.value))} style={{ paddingLeft: '0.75rem' }}>
            <option value={7}>7 giorni</option>
            <option value={14}>14 giorni</option>
            <option value={30}>30 giorni</option>
          </select>
          <input className="input-dark text-sm py-2" placeholder="Note" value={puNotes} onChange={e => setPuNotes(e.target.value)} style={{ paddingLeft: '0.75rem' }} />
        </div>
        <button className="btn-gradient text-sm" onClick={addPowerUser} data-testid="add-power-user-btn"><Plus size={16} /> Aggiungi</button>

        {powerUsers.length > 0 && (
          <div className="mt-4 space-y-2">
            {powerUsers.map(pu => (
              <div key={pu.email} className="hook-item">
                <div className="flex-1">
                  <p className="text-sm font-medium">{pu.email}</p>
                  <p className="text-xs text-[var(--text-muted)]">{pu.plan} | Scade: {new Date(pu.expires_at).toLocaleDateString('it-IT')}</p>
                </div>
                <span className={`badge ${pu.active ? 'green' : 'orange'}`}>{pu.active ? 'Attivo' : 'Disattivo'}</span>
                <button className="btn-ghost p-1.5" onClick={() => togglePu(pu.email)}>
                  {pu.active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                </button>
                <button className="btn-ghost p-1.5" onClick={() => deletePu(pu.email)}><Trash size={16} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Release Notes */}
      <div className="card">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><Megaphone size={20} /> Release Notes</h3>
        <div className="space-y-3 mb-4">
          <div className="flex gap-3">
            <input className="input-dark text-sm py-2 flex-1" placeholder="Titolo" value={rnTitle} onChange={e => setRnTitle(e.target.value)} style={{ paddingLeft: '0.75rem' }} />
            <input className="input-dark text-sm py-2 w-32" placeholder="Versione" value={rnVersion} onChange={e => setRnVersion(e.target.value)} style={{ paddingLeft: '0.75rem' }} />
          </div>
          <textarea className="input-dark text-sm py-2" rows={3} placeholder="Contenuto della nota..." value={rnBody} onChange={e => setRnBody(e.target.value)} style={{ paddingLeft: '0.75rem' }} />
          <button className="btn-gradient text-sm" onClick={createNote} data-testid="create-release-note-btn"><Plus size={16} /> Pubblica Nota</button>
        </div>

        {releaseNotes.length > 0 && (
          <div className="space-y-3 mt-4 pt-4 border-t border-[var(--border-color)]">
            {releaseNotes.map(note => (
              <motion.div key={note.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-semibold text-sm">{note.title}</h4>
                    {note.version && <span className="badge purple text-[10px] mt-1">{note.version}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[var(--text-muted)]">{new Date(note.created_at).toLocaleDateString('it-IT')}</span>
                    <button onClick={() => deleteNote(note.id)} className="text-[var(--accent-pink)]"><Trash size={14} /></button>
                  </div>
                </div>
                <p className="text-sm text-[var(--text-secondary)] whitespace-pre-wrap">{note.body}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

